import * as React from 'react';
import { useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../store';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBars, fetchOrders, SymbolBarsParams } from '../reducers/symbolSlice';
import { getDateString, getStartDate } from '../utils/utils';
import { timeFrameConfig } from '../utils/constants';
import { SymbolPage } from '../routes/symbol-page';
import { fetchPositions } from '../reducers/positionSlice';
import { buildPortfolioTechnicalMetrics } from '../utils/technicalAnalysis';
import { fetchOptionContracts } from '../reducers/optionsSlice';
import { processOptionChain, generateOptionsPrediction, buildHistoricalOptionsStrategyHistory, PastExpirationAnalysis } from '../utils/optionsService';
import axios from 'axios';
import { config } from '../config';
import { Bar } from '../reducers/symbolSlice';
import { OptionChainData, OptionContract, OptionsPrediction } from '../reducers/optionsSlice';

export const SymbolPageContainer: React.FC = () => {
    const { symbol } = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const { symbols } = useSelector((state: RootState) => state.symbols);
    const { positions } = useSelector((state: RootState) => state.alpaca);
    const { marketDates } = useSelector((state: RootState) => state.lookups);
    const marketDateString = React.useMemo(() => {
        let marketDate = new Date(marketDates[marketDates.length - 1].date);
        if (marketDate.getUTCDay() !== new Date().getDay()) {
            marketDate = new Date(marketDates[marketDates.length - 2].date);
        }
        return getDateString(marketDate);
    }, [marketDates]);
    const getChartConfig = React.useCallback((targetSymbol: string, period: string): SymbolBarsParams => {
        let marketDate = new Date(marketDates[marketDates.length - 1].date);
        if (marketDate.getUTCDay() !== new Date().getDay()) {
            marketDate = new Date(marketDates[marketDates.length - 2].date);
        }
        const endDate = `${marketDateString}T17:00:00Z`;
        const selectedPeriod = period as keyof typeof timeFrameConfig;

        if (period === '1D') {
            return {
                symbol: targetSymbol,
                timeframe: timeFrameConfig['1D'],
                period,
                startDate: `${marketDateString}T06:30:00Z`,
                endDate,
            };
        }

        const startDateRef = new Date(marketDate);
        return {
            symbol: targetSymbol,
            timeframe: timeFrameConfig[selectedPeriod],
            period,
            startDate: getStartDate(period, startDateRef),
            endDate,
        };
    }, [marketDateString, marketDates]);
    const [ chartConfig, setChartConfig ] = React.useState<SymbolBarsParams>(() => getChartConfig(symbol, '1D'));

    React.useEffect(() => {
        setChartConfig(current => getChartConfig(symbol, current.period));
    }, [getChartConfig, symbol]);

    const currentChartConfig: SymbolBarsParams = {
        ...chartConfig,
        symbol,
    };
    const [technicalBars, setTechnicalBars] = React.useState<Bar[]>([]);
    const [optionContracts, setOptionContracts] = React.useState<OptionContract[]>([]);
    const [historicalOptionContracts, setHistoricalOptionContracts] = React.useState<OptionContract[]>([]);
    const [expirationDates, setExpirationDates] = React.useState<string[]>([]);
    const [selectedExpirationDate, setSelectedExpirationDate] = React.useState<string>('all');
    const [optionChainData, setOptionChainData] = React.useState<OptionChainData | null>(null);
    const [optionsPrediction, setOptionsPrediction] = React.useState<OptionsPrediction | null>(null);
    const [strategyHistory, setStrategyHistory] = React.useState<PastExpirationAnalysis[]>([]);
    const [strategyHistoryLoading, setStrategyHistoryLoading] = React.useState(false);

    const pastThreeMonthBars = React.useMemo(() => {
        const pastThreeMonthsStart = new Date(marketDateString);
        pastThreeMonthsStart.setMonth(pastThreeMonthsStart.getMonth() - 3);
        return technicalBars.filter((bar) => new Date(bar.t) >= pastThreeMonthsStart);
    }, [marketDateString, technicalBars]);

    React.useEffect(() => {
        dispatch(fetchBars(getChartConfig(symbol, chartConfig.period)));
        dispatch(fetchOrders({
            symbol,
            limit: 500,
            status: 'all'
        }));

        if (!positions.length) {
            dispatch(fetchPositions());
        }

        const fetchTechnicalBars = async () => {
            try {
                const technicalDate = new Date(marketDateString);
                const technicalStartDate = new Date(technicalDate);
                technicalStartDate.setFullYear(technicalStartDate.getFullYear() - 2);

                const result = await axios({
                    baseURL: config.alpaca.dataUrl,
                    method: 'get',
                    url: `v2/stocks/${symbol}/bars?timeframe=1Day&limit=1000&adjustment=raw&feed=iex&sort=asc&start=${getDateString(technicalStartDate)}T06:30:00Z&end=${marketDateString}T17:00:00Z`,
                    headers: {
                        'APCA-API-KEY-ID': config.alpaca.keyId,
                        'APCA-API-SECRET-KEY': config.alpaca.secretKey,
                        'accept': 'application/json'
                    }
                });

                setTechnicalBars(result.data?.bars || []);
            } catch (error) {
                setTechnicalBars([]);
            }
        };

        fetchTechnicalBars();
    }, [dispatch, getChartConfig, marketDateString, positions.length, symbol]);

    // Fetch options data
    React.useEffect(() => {
        const fetchOptionsData = async () => {
            try {
                setOptionContracts([]);
                setHistoricalOptionContracts([]);
                setExpirationDates([]);
                setOptionChainData(null);
                setOptionsPrediction(null);
                setSelectedExpirationDate('all');

                const periodEnd = marketDateString;
                const threeMonthsAgo = new Date(marketDateString);
                threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
                const threeMonthsFromNow = new Date(marketDateString);
                threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
                const threeMonthsAgoStr = threeMonthsAgo.toISOString().split('T')[0];
                const threeMonthsFromNowStr = threeMonthsFromNow.toISOString().split('T')[0];

                const futureResult = await dispatch(fetchOptionContracts({
                    symbol,
                    expirationDateGte: periodEnd,
                    expirationDateLte: threeMonthsFromNowStr,
                    status: 'active',
                })).unwrap();

                const historicalParams = {
                    symbol,
                    expirationDateGte: threeMonthsAgoStr,
                    expirationDateLte: periodEnd,
                };
                const [activeHistorical, inactiveHistorical] = await Promise.all([
                    dispatch(fetchOptionContracts({ ...historicalParams, status: 'active' })).unwrap(),
                    dispatch(fetchOptionContracts({ ...historicalParams, status: 'inactive' })).unwrap(),
                ]);

                const mergedHistoricalContracts = Array.from(
                    new Map(
                        [...activeHistorical.contracts, ...inactiveHistorical.contracts]
                            .map((contract) => [contract.id, contract])
                    ).values()
                );

                if (futureResult.contracts.length > 0) {
                    const futureDates = Array.from(
                        new Set(
                            futureResult.contracts
                                .map((contract) => contract.expiration_date)
                                .filter((date) => date >= periodEnd)
                        )
                    ).sort();
                    setOptionContracts(futureResult.contracts);
                    setExpirationDates(futureDates);
                    setSelectedExpirationDate((current) => current === 'all' || futureDates.includes(current) ? current : 'all');
                } else {
                    setOptionContracts([]);
                    setExpirationDates([]);
                    setOptionChainData(null);
                    setOptionsPrediction(null);
                }

                setHistoricalOptionContracts(mergedHistoricalContracts);
            } catch (error) {
                console.error('Error fetching options data:', error);
                setOptionContracts([]);
                setHistoricalOptionContracts([]);
                setExpirationDates([]);
                setOptionChainData(null);
                setOptionsPrediction(null);
            }
        };

        if (symbol) {
            fetchOptionsData();
        }
    }, [symbol, dispatch, marketDateString]);

    React.useEffect(() => {
        if (!optionContracts.length || !symbols[symbol]?.bars?.length) {
            setOptionChainData(null);
            setOptionsPrediction(null);
            return;
        }

        const selectedDate = selectedExpirationDate === 'all' ? undefined : selectedExpirationDate;
        const chain = processOptionChain(optionContracts, symbol, selectedDate);
        chain.expiration_date = selectedDate || 'next 3 months';
        chain.expiration_dates = expirationDates;
        setOptionChainData(chain);

        const currentPrice = symbols[symbol].bars[symbols[symbol].bars.length - 1]?.c || 0;
        setOptionsPrediction(generateOptionsPrediction(chain, currentPrice));
    }, [expirationDates, optionContracts, selectedExpirationDate, symbol, symbols]);

    React.useEffect(() => {
        if (!symbol || pastThreeMonthBars.length < 2 || !historicalOptionContracts.length) {
            setStrategyHistory([]);
            return;
        }

        let cancelled = false;
        setStrategyHistoryLoading(true);

        buildHistoricalOptionsStrategyHistory(symbol, pastThreeMonthBars, historicalOptionContracts)
            .then((history) => {
                if (!cancelled) {
                    setStrategyHistory(history);
                }
            })
            .catch((error) => {
                console.error('Error building historical options strategy:', error);
                if (!cancelled) {
                    setStrategyHistory([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setStrategyHistoryLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [symbol, pastThreeMonthBars, historicalOptionContracts]);

    const updateChart = (period: string) => {
        const config = getChartConfig(symbol, period);
        setChartConfig(config);
        dispatch(fetchBars(config));
    }

    let profitLoss = 0;
    let position = null;
    if (!positions.length || !symbols[symbol] || !symbols[symbol].bars || !symbols[symbol].orders){
        return <p>Loading...</p>;
    } else {
        const technicalMetrics = technicalBars.length ? buildPortfolioTechnicalMetrics(technicalBars) : null;
        position = positions.find(p => p.symbol === symbol);
        profitLoss = (position ? parseFloat(position.market_value) : 0) - symbols[symbol].orders.reduce((result, order) => {
            const filled_qty = parseFloat(order.filled_qty);
            const filled_avg_price = parseFloat(order.filled_avg_price);
            if (filled_qty > 0) {
                if (order.side === 'buy') {
                    result += filled_qty * filled_avg_price;
                }
                if (order.side === 'sell') {
                    result -= filled_qty * filled_avg_price;
                }
            }
            return result;
        }, profitLoss);
    
        return (
            <SymbolPage
                symbol={symbol}
                position={position}
                profitLoss={profitLoss}
                data={symbols[symbol]}
                chartPeriod={currentChartConfig.period}
                updateChart={updateChart}
                technicalMetrics={technicalMetrics}
                optionChainData={optionChainData}
                optionsPrediction={optionsPrediction}
                expirationDates={expirationDates}
                selectedExpirationDate={selectedExpirationDate}
                onExpirationDateChange={setSelectedExpirationDate}
                pastThreeMonthBars={pastThreeMonthBars}
                strategyHistory={strategyHistory}
                strategyHistoryLoading={strategyHistoryLoading}
                marketDate={marketDateString}
            />
        );
        
    }
};